import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { US_OFFERS_SCHEMA } from '../schemas/UsOffers';
import { PromotionType } from '../types/offers';
import { formatDate } from '../utils/formatDate';
import { OFFER_MONTH } from '../constants/environment';
import { selectZipCode } from '../reducers/zipCodeReducer';

interface QueryResult {
  usCustomerOffers: PromotionType[];
}

// Custom hook or function to handle the query logic
const useFetchOffers = (zipCode?: string) => {
  const { market } = useSelector(selectZipCode);

  const isCaMarket = market === 'CA';
  const { data, loading, error } = useQuery<
    QueryResult,
    {
      zipCode?: string;
      offerMonth?: string;
    }
  >(US_OFFERS_SCHEMA, {
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
    skip: isCaMarket,
    variables: {
      zipCode,
      offerMonth: OFFER_MONTH,
    },
  });

  const offers =
    data?.usCustomerOffers &&
    data?.usCustomerOffers
      .map((offer) => {
        return {
          ...offer,
          offerType: offer.offerType.toLowerCase(),
          programToDate: offer?.programToDate ? formatDate(offer.programToDate) : '',
          programFromDate: offer?.programFromDate ? formatDate(offer.programFromDate) : '',
        };
      })
      .map((offer) => {
        const isSportbackBody = offer.offerModelDetail.modelCodeDescription.includes('Sportback');
        const isHybrid = offer.offerModelDetail.modelCodeDescription.includes('TFSI e');
        const isRWD = offer.offerModelDetail.modelCodeDescription.includes('RWD');
        const isFWD = offer.offerModelDetail.modelCodeDescription.includes('FWD');

        const correctDrivetrain = () => {
          if (isRWD) return 'rwd';
          if (isFWD) return 'fwd';
          return null;
        };

        const { bodyType } = offer.offerModelDetail;
        return {
          ...offer,
          offerModelDetail: {
            ...offer.offerModelDetail,
            bodyType: isSportbackBody ? 'Sportback' : bodyType,
            fuelType: isHybrid ? 'hybrid' : null,
            drivetrain: correctDrivetrain(),
          },
        };
      });

  return {
    loading,
    error,
    offers,
  };
};

export default useFetchOffers;
